import React, { useEffect, useState, useRef } from 'react';
import './AnalyticsPage.css';
import {
	Layout,
	Card,
	Table,
	Button,
	Space,
	Select,
	Checkbox,
	Typography,
	Row,
	Col,
	Avatar,
	Badge,
	Dropdown,
	Spin,
} from 'antd';
import {
	FilterOutlined,
	UserOutlined,
	PictureOutlined,
	HeartOutlined,
	MessageOutlined,
	EyeOutlined,
	TeamOutlined,
	LinkOutlined,
	DownloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import LineChart from '../../components/Charts/LineChart';
import { fetchAccounts, fetchSnapshots, fetchSummary, fetchRecentMedia } from '../../api/analytics';
import { ensureAccountToday } from '../../api/analytics';
import { useTranslation } from 'react-i18next';
import { fetchWrapper } from '../../helpers';
import { ImeanHeader } from '../../components/Header/Header';

const { Header, Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

const AnalyticsPage = () => {
	const { t } = useTranslation();
	const ENABLE_PDF = true; // включаем кнопку скачивания PDF
	const [loading, setLoading] = useState(true);
	const [accounts, setAccounts] = useState([]);
	const [selectedIg, setSelectedIg] = useState(null);
	const [modeModerator, setModeModerator] = useState(false);
	const [moderatorOrg, setModeratorOrg] = useState(false);
	const [allMode, setAllMode] = useState(false);
	const [followersSeries, setFollowersSeries] = useState([]);
	const [postsSeries, setPostsSeries] = useState([]);
	const [reachSeries, setReachSeries] = useState([]);
	const [engagedSeries, setEngagedSeries] = useState([]);
	const [profileViewsSeries, setProfileViewsSeries] = useState([]);
	const [activeMetric, setActiveMetric] = useState('followers');
	const [summary, setSummary] = useState(null);
	const [dateFrom, setDateFrom] = useState(dayjs().subtract(6, 'day'));
	const [dateTo, setDateTo] = useState(dayjs().subtract(1, 'day'));
	const [recentMedia, setRecentMedia] = useState([]);
	const [recentLoading, setRecentLoading] = useState(false);
	const [showFilters, setShowFilters] = useState(false);
	const filtersRef = useRef(null);
	const [isMobile, setIsMobile] = useState(
		typeof window !== 'undefined' ? window.innerWidth < 680 : false,
	);
	const [isTablet, setIsTablet] = useState(
		typeof window !== 'undefined' ? window.innerWidth < 900 : false,
	);
	const [pdfLoading, setPdfLoading] = useState(false);
	const [hasNoPermission, setHasNoPermission] = useState(false);

	useEffect(() => {
		function onResize() {
			setIsMobile(window.innerWidth < 680);
			setIsTablet(window.innerWidth < 900);
		}
		window.addEventListener('resize', onResize);
		return () => window.removeEventListener('resize', onResize);
	}, []);

	useEffect(() => {
		if (!showFilters) return;
		function onDocClick(e) {
			if (filtersRef.current && !filtersRef.current.contains(e.target)) {
				setShowFilters(false);
			}
		}
		document.addEventListener('mousedown', onDocClick);
		return () => document.removeEventListener('mousedown', onDocClick);
	}, [showFilters]);

	const formatNumber = (n) => {
		if (n == null || n === '' || isNaN(Number(n))) return '-';
		return Number(n).toLocaleString('ru-RU');
	};

	// Load accounts
	useEffect(() => {
		let active = true;
		async function loadAccounts() {
			setLoading(true);
			try {
				const { accounts: accs, moderatorOrg: modOrg } = await fetchAccounts();
				if (!active) return;
				setAccounts(accs);
				setModeratorOrg(modOrg);
				if (accs.length) setSelectedIg(accs[0].ig_id);
			} catch (e) {
				if (active) console.error(e);
			} finally {
				if (active) setLoading(false);
			}
		}
		loadAccounts();
		return () => {
			active = false;
		};
	}, []);

	// Load summary + snapshots when account or dates change
	useEffect(() => {
		if (!selectedIg && !allMode) return;
		let active = true;
		async function loadData() {
			setLoading(true);
			try {
				let followersSnap, postsSnap, reachSnap, engagedSnap, profileViewsSnap, sum;
				if (allMode && modeModerator) {
					const ig_ids = accounts.map((a) => a.ig_id);
					[sum, followersSnap, postsSnap, reachSnap, engagedSnap, profileViewsSnap] =
						await Promise.all([
							fetchSummary({ ig_ids, aggregate: true }),
							fetchSnapshots({
								ig_ids,
								metric: 'followers_count',
								from: dateFrom.format('YYYY-MM-DD'),
								to: dateTo.format('YYYY-MM-DD'),
								fill: true,
								aggregate: true,
							}),
							fetchSnapshots({
								ig_ids,
								metric: 'media_count',
								from: dateFrom.format('YYYY-MM-DD'),
								to: dateTo.format('YYYY-MM-DD'),
								fill: true,
								aggregate: true,
							}),
							fetchSnapshots({
								ig_ids,
								metric: 'reach',
								from: dateFrom.format('YYYY-MM-DD'),
								to: dateTo.format('YYYY-MM-DD'),
								fill: true,
								aggregate: true,
							}),
							fetchSnapshots({
								ig_ids,
								metric: 'account_engaged',
								from: dateFrom.format('YYYY-MM-DD'),
								to: dateTo.format('YYYY-MM-DD'),
								fill: true,
								aggregate: true,
							}),
							fetchSnapshots({
								ig_ids,
								metric: 'profile_views',
								from: dateFrom.format('YYYY-MM-DD'),
								to: dateTo.format('YYYY-MM-DD'),
								fill: true,
								aggregate: true,
							}),
						]);
					if (!active) return;
					setSummary(sum.summary);
					const mapSeries = (snap) =>
						snap.points.map((p) => ({ date: dayjs(p.date).format('DD.MM'), value: p.value }));
					setFollowersSeries(mapSeries(followersSnap));
					setPostsSeries(mapSeries(postsSnap));
					setReachSeries(mapSeries(reachSnap));
					setEngagedSeries(mapSeries(engagedSnap));
					setProfileViewsSeries(mapSeries(profileViewsSnap));
				} else {
					[sum, followersSnap, postsSnap, reachSnap, engagedSnap, profileViewsSnap] =
						await Promise.all([
							fetchSummary(selectedIg),
							fetchSnapshots({
								ig_id: selectedIg,
								metric: 'followers_count',
								from: dateFrom.format('YYYY-MM-DD'),
								to: dateTo.format('YYYY-MM-DD'),
								fill: true,
							}),
							fetchSnapshots({
								ig_id: selectedIg,
								metric: 'media_count',
								from: dateFrom.format('YYYY-MM-DD'),
								to: dateTo.format('YYYY-MM-DD'),
								fill: true,
							}),
							fetchSnapshots({
								ig_id: selectedIg,
								metric: 'reach',
								from: dateFrom.format('YYYY-MM-DD'),
								to: dateTo.format('YYYY-MM-DD'),
								fill: true,
							}),
							fetchSnapshots({
								ig_id: selectedIg,
								metric: 'account_engaged',
								from: dateFrom.format('YYYY-MM-DD'),
								to: dateTo.format('YYYY-MM-DD'),
								fill: true,
							}),
							fetchSnapshots({
								ig_id: selectedIg,
								metric: 'profile_views',
								from: dateFrom.format('YYYY-MM-DD'),
								to: dateTo.format('YYYY-MM-DD'),
								fill: true,
							}),
						]);
					if (!active) return;
					setSummary(sum.summary);
					const mapSeries = (snap) =>
						snap.points.map((p) => ({ date: dayjs(p.date).format('DD.MM'), value: p.value }));
					setFollowersSeries(mapSeries(followersSnap));
					setPostsSeries(mapSeries(postsSnap));
					setReachSeries(mapSeries(reachSnap));
					setEngagedSeries(mapSeries(engagedSnap));
					setProfileViewsSeries(mapSeries(profileViewsSnap));
				}
			} catch (e) {
				if (active) console.error(e);
			} finally {
				if (active) setLoading(false);
			}
		}
		loadData();
		return () => {
			active = false;
		};
	}, [selectedIg, dateFrom, dateTo, allMode, modeModerator]);

	// Load recent media
	useEffect(() => {
		if (!selectedIg && !allMode) return;
		let active = true;
		async function loadRecent() {
			setRecentLoading(true);
			try {
				let data;
				if (allMode && modeModerator) {
					const ig_ids = accounts.map((a) => a.ig_id);
					data = await fetchRecentMedia({
						ig_ids,
						from: dateFrom.format('YYYY-MM-DD'),
						to: dateTo.format('YYYY-MM-DD'),
						aggregate: true,
					});
				} else {
					data = await fetchRecentMedia({
						ig_id: selectedIg,
						from: dateFrom.format('YYYY-MM-DD'),
						to: dateTo.format('YYYY-MM-DD'),
					});
				}
				if (!active) return;
				const rows = (data.posts || []).map((p) => {
					const rawLikes = p.like_count;
					const rawComments = p.comments_count;
					const toNum = (v) => {
						if (v == null) return 0;
						if (typeof v === 'number') return v;
						if (typeof v === 'string') {
							const digits = v.replace(/[^0-9.-]/g, '');
							const n = parseInt(digits, 10);
							return isNaN(n) ? 0 : n;
						}
						return 0;
					};
					return {
						key: p.media_id,
						media_id: p.media_id,
						media_type: p.media_type,
						media_product_type: p.media_product_type,
						published_at: p.published_at,
						published_fmt: p.published_at ? dayjs(p.published_at).format('DD/MM/YYYY') : '-',
						caption: p.caption || '',
						like_count: toNum(rawLikes),
						comments_count: toNum(rawComments),
						permalink: p.permalink,
						thumbnail_url: p.thumbnail_url || p.media_url,
						media_url: p.media_url || null,
						account_username: p.account_username || null,
					};
				});
				setRecentMedia(rows);
			} finally {
				setRecentLoading(false);
			}
		}
		loadRecent();
		return () => {
			active = false;
		};
	}, [selectedIg, allMode, modeModerator, dateFrom, dateTo]);

	const currentAccount = accounts.find((a) => a.ig_id === selectedIg);
	const totalLikes = recentMedia.reduce((s, r) => s + r.like_count, 0);
	const totalComments = recentMedia.reduce((s, r) => s + r.comments_count, 0);
	const likesTotal = typeof summary?.likes_sum === 'number' ? summary.likes_sum : totalLikes;
	const commentsTotal =
		typeof summary?.comments_sum === 'number' ? summary.comments_sum : totalComments;

	// Доп. подзагрузка пер-аккаунтных рядов для активной метрики в режиме ALL (для тултипа-брейкдауна)
	useEffect(() => {
		if (!allMode || !modeModerator) return;
		if (!accounts.length) return;
		let cancelled = false;

		const metricKey = (() => {
			switch (activeMetric) {
				case 'posts':
					return 'media_count';
				case 'reach':
					return 'reach';
				case 'engaged':
					return 'account_engaged';
				case 'profile_views':
					return 'profile_views';
				default:
					return 'followers_count';
			}
		})();

		(async () => {
			try {
				const from = dateFrom.format('YYYY-MM-DD');
				const to = dateTo.format('YYYY-MM-DD');
				const perAcc = await Promise.all(
					accounts.map(async (a) => {
						const snap = await fetchSnapshots({
							ig_id: a.ig_id,
							metric: metricKey,
							from,
							to,
							fill: true,
						});
						const byDate = new Map();
						for (const p of snap.points || []) {
							byDate.set(
								p.date,
								typeof p.value === 'number' ? p.value : p.value == null ? null : Number(p.value),
							);
						}
						return { ig_id: a.ig_id, username: a.username, byDate };
					}),
				);

				const dates = [];
				let cursor = dateFrom.startOf('day');
				const end = dateTo.startOf('day');
				while (cursor.isSameOrBefore(end, 'day')) {
					dates.push(cursor.format('YYYY-MM-DD'));
					cursor = cursor.add(1, 'day');
				}

				const series = dates.map((d) => {
					const breakdown = perAcc.map((pa) => ({
						username: pa.username,
						value: pa.byDate.get(d) ?? null,
					}));
					const total = breakdown.reduce(
						(s, b) => s + (typeof b.value === 'number' ? b.value : 0),
						0,
					);
					breakdown.sort((a, b) => (b.value || 0) - (a.value || 0));
					return { date: dayjs(d).format('DD.MM'), value: total, breakdown };
				});

				if (cancelled) return;
				switch (activeMetric) {
					case 'posts':
						setPostsSeries(series);
						break;
					case 'reach':
						setReachSeries(series);
						break;
					case 'engaged':
						setEngagedSeries(series);
						break;
					case 'profile_views':
						setProfileViewsSeries(series);
						break;
					default:
						setFollowersSeries(series);
						break;
				}
			} catch (e) {
				console.error('breakdown load error', e);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [allMode, modeModerator, accounts, dateFrom, dateTo, activeMetric]);

	// Кастомный тултип для графика: показываем только общее значение с локализацией
	const renderTooltip = ({ active, payload, label }) => {
		if (!active || !payload || !payload.length) return null;
		const point = payload[0]?.payload || {};

		return (
			<div
				style={{
					background: '#fff',
					border: '1px solid #e5e7eb',
					borderRadius: 8,
					padding: 8,
					boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
				}}
			>
				<div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
				<div>
					{t('analytics.total') || 'Суммарно'}: {formatNumber(point.value)}
				</div>
			</div>
		);
	};

	// PDF download handler
	const handleDownloadPdf = async () => {
		try {
			setPdfLoading(true);
			const API = process.env.REACT_APP_API_URL || '';
			const params = new URLSearchParams();
			// Always use a 30-day window per requirements, regardless of current UI selection
			const to = dayjs().format('YYYY-MM-DD');
			const from = dayjs().subtract(29, 'day').format('YYYY-MM-DD');
			params.set('from', from);
			params.set('to', to);
			if (allMode && modeModerator) {
				const ig_ids = accounts.map((a) => a.ig_id).filter(Boolean);
				if (!ig_ids.length) throw new Error('Нет доступных аккаунтов');
				params.set('ig_ids', ig_ids.join(','));
			} else if (selectedIg) {
				params.set('ig_id', selectedIg);
			} else {
				throw new Error('Не выбран аккаунт');
			}
			const url = `${API}/analytics/report-pdf?${params.toString()}`;
			const response = await fetchWrapper.get(url, null, { raw: true });
			if (!response.ok) {
				let text;
				try {
					text = await response.text();
				} catch {
					text = response.statusText;
				}
				throw new Error(text || 'PDF download failed');
			}
			const blob = await response.blob();
			const dlUrl = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = dlUrl;
			const baseName =
				allMode && modeModerator
					? 'all_accounts'
					: currentAccount?.username || selectedIg || 'account';
			link.download = `analytics_${baseName}_${from.replaceAll('-', '')}_${to.replaceAll(
				'-',
				'',
			)}.pdf`;
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(dlUrl);
		} catch (e) {
			console.error('PDF download error', e);
		} finally {
			setPdfLoading(false);
		}
	};

	// Filter dropdown items — конец на «вчера»
	const filterItems = [
		{
			key: '7days',
			label: (
				<div
					onClick={() => {
						const end = dayjs().subtract(1, 'day');
						setDateTo(end);
						setDateFrom(end.clone().subtract(6, 'day'));
					}}
				>
					{t('analytics.days7')}
				</div>
			),
		},
		{
			key: '30days',
			label: (
				<div
					onClick={() => {
						const end = dayjs().subtract(1, 'day');
						setDateTo(end);
						setDateFrom(end.clone().subtract(29, 'day'));
					}}
				>
					{t('analytics.days30')}
				</div>
			),
		},
	];

	// Auto ensure today snapshot if summary null after first load
	useEffect(() => {
		if (!loading && selectedIg && summary === null) {
			let cancelled = false;
			(async () => {
				try {
					const { insights_ok } = await ensureAccountToday(selectedIg);
					if (cancelled) return;

					if (insights_ok === false) {
						throw new Error('INSIGHTS_FETCH_FAILED');
					}
					// refetch summary only
					const sum = await fetchSummary(selectedIg);
					if (cancelled) return;
					setSummary(sum.summary);
				} catch (e) {
					if (e.message === 'INSIGHTS_FETCH_FAILED') {
						setHasNoPermission(true);
					}
				}
			})();
			return () => {
				cancelled = true;
			};
		}
	}, [loading, selectedIg, summary]);

	if (!loading && accounts.length === 0) {
		return (
			<Layout
				style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #ffffff, #faf5ff)' }}
			>
				<Content
					style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						minHeight: '100vh',
					}}
				>
					<Card style={{ maxWidth: 480, width: '100%', textAlign: 'center' }}>
						<Space direction="vertical" size={24}>
							<Badge color="#7c3aed" />
							<Title level={2}>
								{t('analytics.title')}
								{modeModerator ? ' | ' + (t('analytics.moderatorTag') || 'Moderator') : ''}
							</Title>
							<Text type="secondary">{t('analytics.noAccounts')}</Text>
							<Text>{t('analytics.emptyLead')}</Text>
							<Button type="primary" size="large" href="/profile">
								{t('analytics.connectAccount')}
							</Button>
						</Space>
					</Card>
				</Content>
			</Layout>
		);
	}

	return (
		<>
			<ImeanHeader initialDark={true} />
			<Layout
				style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #ffffff, #faf5ff)' }}
			>
				{/* Header */}
				<Header
					style={{
						position: 'sticky',
						top: 0,
						zIndex: 10,
						background: 'rgba(255,255,255,0.8)',
						backdropFilter: 'blur(6px)',
						borderBottom: '1px solid #f0f0f0',
						padding: 0,
					}}
				>
					<div
						style={{
							maxWidth: 1200,
							margin: '0 auto',
							width: '100%',
							padding: isMobile ? '0 16px' : '0 24px',
						}}
					>
						<Row align="middle" justify="space-between">
							<Space align="center" size={12}>
								{currentAccount?.profile_picture_url && !allMode ? (
									<Avatar src={currentAccount.profile_picture_url} size={40} />
								) : null}
								<div>
									<Title level={4} style={{ margin: 0, lineHeight: 1.1 }}>
										{t('analytics.title')}
										{modeModerator ? ' | ' + (t('analytics.moderatorTag') || 'Moderator') : ''}
									</Title>
									{currentAccount && !allMode && (
										<Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 2 }}>
											{t('analytics.account')} @{currentAccount.username}
										</Text>
									)}
								</div>
							</Space>

							{!(isMobile || isTablet) && (
								<Space align="center" size={12} wrap>
									{modeModerator && (
										<Select
											value={allMode ? 'ALL' : selectedIg || ''}
											onChange={(value) => {
												if (value === 'ALL') {
													setAllMode(true);
												} else {
													setAllMode(false);
													setSelectedIg(value);
												}
											}}
											style={{ minWidth: 150 }}
										>
											{accounts.map((a) => (
												<Option key={a.ig_id} value={a.ig_id}>
													{a.username}
												</Option>
											))}
											<Option value="ALL">{t('analytics.allAccounts') || 'All accounts'}</Option>
										</Select>
									)}

									{!modeModerator && accounts.length > 1 && (
										<Select
											value={selectedIg || ''}
											onChange={setSelectedIg}
											style={{ minWidth: 150 }}
										>
											{accounts.map((a) => (
												<Option key={a.ig_id} value={a.ig_id}>
													{a.username}
												</Option>
											))}
										</Select>
									)}

									{moderatorOrg && (
										<Checkbox
											checked={modeModerator}
											onChange={(e) => {
												setModeModerator(e.target.checked);
												setAllMode(false);
											}}
											style={{
												'--checkbox-color': '#9333ea', // фиолетовый цвет
											}}
											className="moderator-checkbox"
										>
											{t('analytics.moderatorMode')}
										</Checkbox>
									)}

									<Dropdown menu={{ items: filterItems }} placement="bottomRight">
										<Button icon={<FilterOutlined />}>{t('analytics.filters')}</Button>
									</Dropdown>

									{ENABLE_PDF && (
										<Button
											icon={<DownloadOutlined />}
											loading={pdfLoading}
											onClick={handleDownloadPdf}
										>
											{pdfLoading
												? t('analytics.downloading') || 'Скачиваем...'
												: t('analytics.downloadPdf') || 'Скачать PDF-отчет'}
										</Button>
									)}
								</Space>
							)}
						</Row>
					</div>
				</Header>

				<Content style={{ maxWidth: 1200, margin: '24px auto', width: '100%', padding: '0 16px' }}>
					{/* Mobile Controls */}
					{(isMobile || isTablet) && (
						<Card style={{ marginBottom: 16 }}>
							<Space direction="vertical" style={{ width: '100%' }}>
								{moderatorOrg && (
									<Checkbox
										checked={modeModerator}
										onChange={(e) => {
											setModeModerator(e.target.checked);
											setAllMode(false);
										}}
										style={{
											'--checkbox-color': '#9333ea', // фиолетовый цвет
										}}
										className="moderator-checkbox"
									>
										{t('analytics.moderatorMode')}
									</Checkbox>
								)}

								{modeModerator ? (
									<Select
										value={allMode ? 'ALL' : selectedIg || ''}
										onChange={(value) => {
											if (value === 'ALL') {
												setAllMode(true);
											} else {
												setAllMode(false);
												setSelectedIg(value);
											}
										}}
										style={{ width: '100%' }}
									>
										{accounts.map((a) => (
											<Option key={a.ig_id} value={a.ig_id}>
												{a.username}
											</Option>
										))}
										<Option value="ALL">{t('analytics.allAccounts') || 'All accounts'}</Option>
									</Select>
								) : (
									accounts.length > 1 && (
										<Select
											value={selectedIg || ''}
											onChange={setSelectedIg}
											style={{ width: '100%' }}
										>
											{accounts.map((a) => (
												<Option key={a.ig_id} value={a.ig_id}>
													{a.username}
												</Option>
											))}
										</Select>
									)
								)}

								<Space style={{ width: '100%', justifyContent: 'space-between' }}>
									<Dropdown menu={{ items: filterItems }} placement="bottomLeft">
										<Button icon={<FilterOutlined />}>{t('analytics.filters')}</Button>
									</Dropdown>
									{ENABLE_PDF && (
										<Button
											icon={<DownloadOutlined />}
											loading={pdfLoading}
											onClick={handleDownloadPdf}
										>
											{pdfLoading
												? t('analytics.downloading') || 'Скачиваем...'
												: t('analytics.downloadPdf') || 'Скачать PDF-отчет'}
										</Button>
									)}
								</Space>
							</Space>
						</Card>
					)}

					<Spin spinning={loading}>
						{/* Stats Cards */}
						{summary && (
							<Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
								<Col xs={12} md={6}>
									<StatCard
										icon={<TeamOutlined />}
										title={t('analytics.followers')}
										value={formatNumber(summary.followers_count)}
										subtitle={t('analytics.followersTotalSub')}
									/>
								</Col>
								<Col xs={12} md={6}>
									<StatCard
										icon={<PictureOutlined />}
										title={t('analytics.media')}
										value={formatNumber(summary.media_count)}
										subtitle={t('analytics.mediaTotalSub')}
									/>
								</Col>
								<Col xs={12} md={6}>
									<StatCard
										icon={<HeartOutlined />}
										title={t('analytics.likesSum')}
										value={formatNumber(likesTotal)}
										subtitle={
											allMode
												? t('analytics.lastPostsAll', { count: 30 }) || 'Last 30 merged posts'
												: t('analytics.last10Posts')
										}
									/>
								</Col>
								<Col xs={12} md={6}>
									<StatCard
										icon={<MessageOutlined />}
										title={t('analytics.commentsSum')}
										value={formatNumber(commentsTotal)}
										subtitle={
											allMode
												? t('analytics.lastPostsAll', { count: 30 }) || 'Last 30 merged posts'
												: t('analytics.last10Posts')
										}
									/>
								</Col>

								{typeof summary.reach === 'number' && (
									<Col xs={12} md={6}>
										<StatCard
											icon={<EyeOutlined />}
											title={t('analytics.reach')}
											value={formatNumber(summary.reach)}
											subtitle={
												summary.reach_diff != null ? `Δ ${formatNumber(summary.reach_diff)}` : null
											}
										/>
									</Col>
								)}

								{typeof summary.account_engaged === 'number' && (
									<Col xs={12} md={6}>
										<StatCard
											icon={<UserOutlined />}
											title={t('analytics.engagement')}
											value={formatNumber(summary.account_engaged)}
											subtitle={
												summary.account_engaged_diff != null
													? `Δ ${formatNumber(summary.account_engaged_diff)}`
													: null
											}
										/>
									</Col>
								)}

								{typeof summary.profile_views === 'number' && (
									<Col xs={12} md={6}>
										<StatCard
											icon={<EyeOutlined />}
											title={t('analytics.profileViews')}
											value={formatNumber(summary.profile_views)}
											subtitle={
												summary.profile_views_diff != null
													? `Δ ${formatNumber(summary.profile_views_diff)}`
													: null
											}
										/>
									</Col>
								)}
							</Row>
						)}

						{/* Chart */}
						<Card title={t('analytics.metricSelector')} style={{ marginBottom: 24 }}>
							<Space wrap style={{ marginBottom: 16 }}>
								{[
									{ key: 'followers', label: t('analytics.metricFollowers') },
									{ key: 'posts', label: t('analytics.metricPosts') },
									{ key: 'reach', label: t('analytics.metricReach') },
									{ key: 'engaged', label: t('analytics.metricEngaged') },
									{ key: 'profile_views', label: t('analytics.metricProfileViews') },
								].map((btn) => (
									<Button
										key={btn.key}
										type={activeMetric === btn.key ? 'primary' : 'default'}
										onClick={() => setActiveMetric(btn.key)}
										size="small"
									>
										{btn.label}
									</Button>
								))}
							</Space>

							<LineChart
								label={(() => {
									switch (activeMetric) {
										case 'posts':
											return t('analytics.posts');
										case 'reach':
											return t('analytics.reach');
										case 'engaged':
											return t('analytics.engagement');
										case 'profile_views':
											return t('analytics.profileViews');
										default:
											return t('analytics.followersByDay');
									}
								})()}
								data={(() => {
									switch (activeMetric) {
										case 'posts':
											return postsSeries;
										case 'reach':
											return reachSeries;
										case 'engaged':
											return engagedSeries;
										case 'profile_views':
											return profileViewsSeries;
										default:
											return followersSeries;
									}
								})()}
								xKey="date"
								yKey="value"
								color={(() => {
									switch (activeMetric) {
										case 'posts':
											return '#3b82f6';
										case 'reach':
											return '#10b981';
										case 'engaged':
											return '#f59e0b';
										case 'profile_views':
											return '#ef4444';
										default:
											return '#6d28d9';
									}
								})()}
								formatTooltipValue={(v) => `${v}`}
								customTooltip={renderTooltip}
							/>
						</Card>

						{/* Recent Posts Table */}
						<Card
							title={
								allMode
									? t('analytics.recentPostsAll') || t('analytics.recentPosts')
									: t('analytics.recentPosts')
							}
						>
							<Table
								size="middle"
								loading={recentLoading}
								dataSource={recentMedia}
								pagination={false}
								locale={{ emptyText: t('analytics.noPosts') }}
								rowKey="media_id"
								scroll={{ x: 800 }}
								columns={[
									{
										title: t('analytics.date'),
										dataIndex: 'published_fmt',
										sorter: (a, b) =>
											dayjs(a.published_at).valueOf() - dayjs(b.published_at).valueOf(),
										defaultSortOrder: 'descend',
										width: 110,
									},
									allMode
										? {
												title: t('analytics.account') + ' / ' + t('analytics.post'),
												dataIndex: 'account_username',
												render: (_, record) => (
													<Space align="start">
														<MediaThumb record={record} />
														<div style={{ maxWidth: 300 }}>
															<Text strong style={{ display: 'block' }}>
																@{record.account_username || '?'}
															</Text>
															{record.caption ? (
																<Text
																	style={{ fontSize: 12 }}
																	ellipsis={{ tooltip: record.caption }}
																>
																	{record.caption}
																</Text>
															) : (
																<Text type="secondary" style={{ fontSize: 12 }}>
																	—
																</Text>
															)}
														</div>
													</Space>
												),
										  }
										: {
												title: t('analytics.post'),
												dataIndex: 'caption',
												render: (_, record) => (
													<Space align="start">
														<MediaThumb record={record} />
														<div style={{ maxWidth: 500 }}>
															{record.caption ? (
																<Text style={{ fontSize: 12 }} ellipsis={true}>
																	{record.caption}
																</Text>
															) : (
																<Text type="secondary" style={{ fontSize: 12 }}>
																	—
																</Text>
															)}
														</div>
													</Space>
												),
										  },
									{
										title: t('analytics.likes'),
										dataIndex: 'like_count',
										sorter: (a, b) => a.like_count - b.like_count,
										width: 90,
										render: (v) => formatNumber(v),
									},
									{
										title: t('analytics.comments'),
										dataIndex: 'comments_count',
										sorter: (a, b) => a.comments_count - b.comments_count,
										width: 110,
										render: (v) => formatNumber(v),
									},
									{
										title: t('analytics.link'),
										dataIndex: 'permalink',
										width: 90,
										render: (v) =>
											v ? (
												<Button
													type="link"
													icon={<LinkOutlined />}
													href={v}
													target="_blank"
													rel="noreferrer"
													size="small"
												>
													{t('analytics.open')}
												</Button>
											) : (
												'-'
											),
									},
									{
										title: 'Insights',
										dataIndex: 'media_id',
										width: 120,
										fixed: false,
										render: (_, record) => (
											<Button
												type="link"
												size="small"
												onClick={() => {
													window.location.href = `/analytics/media/${record.media_id}?p=7d`;
												}}
											>
												Открыть
											</Button>
										),
									},
								]}
							/>
						</Card>
					</Spin>
				</Content>
			</Layout>
		</>
	);
};

// Stat Card Component
// Safe thumbnail that tries multiple sources before falling back
const MediaThumb = ({ record, size = 40 }) => {
	const srcs = [record?.thumbnail_url, record?.media_url].filter(Boolean);
	const [idx, setIdx] = React.useState(0);
	const current = srcs[idx];
	if (!current) {
		return <Avatar icon={<PictureOutlined />} size={size} shape="square" />;
	}
	return (
		<img
			src={current}
			alt=""
			style={{
				width: size,
				height: size,
				borderRadius: 4,
				objectFit: 'cover',
				background: '#f5f5f5',
			}}
			onError={() => {
				if (idx < srcs.length - 1) setIdx(idx + 1);
			}}
			referrerPolicy="no-referrer"
			crossOrigin="anonymous"
		/>
	);
};

const StatCard = ({ icon, title, value, subtitle }) => (
	<Card size="small" className="shadow-sm">
		<Space direction="vertical" style={{ width: '100%' }}>
			<Space align="center">
				<div
					style={{
						width: 32,
						height: 32,
						borderRadius: 8,
						background: 'rgba(124, 58, 237, 0.1)',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						color: '#7c3aed',
					}}
				>
					{icon}
				</div>
				<div>
					<Text type="secondary" style={{ fontSize: 12 }}>
						{title}
					</Text>
					<div>
						<Text strong style={{ fontSize: 18, color: '#7c3aed' }}>
							{value}
						</Text>
					</div>
				</div>
			</Space>
			{subtitle && (
				<Text type="secondary" style={{ fontSize: 11 }}>
					{subtitle}
				</Text>
			)}
		</Space>
	</Card>
);

export default AnalyticsPage;
